import getConfiguration from '@/store/getConfiguration';
import { BookingApi, BookingDTO, WriteBookingRequest } from '@/api';
import { showError, showSuccess } from '@/mixins/toastService';
import { format } from 'date-fns';
import * as Sentry from '@sentry/vue';

async function getApi() {
  const configuration = await getConfiguration();
  return new BookingApi(configuration, '');
}

type InitialState = {
  bookings: BookingDTO[];
  selectedWeek?: {
    startDate: Date;
    endDate: Date;
  };
  selectedDockIds?: string[];
  isLoadingBookings: boolean;
  fetchAbortController: AbortController | null;
};

export const initialState = (): InitialState => ({
  bookings: [],
  isLoadingBookings: false,
  selectedDockIds: undefined,
  selectedWeek: undefined,
  fetchAbortController: null,
});
export type DockBookingState = ReturnType<typeof initialState>;
type ActionType = {
  commit: Function;
  state: DockBookingState;
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    resetState(state: DockBookingState) {
      Object.assign(state, initialState());
    },

    setBookings(state: DockBookingState, bookings: BookingDTO[]) {
      state.bookings = bookings;
    },
    setLoadingBookings(state: DockBookingState, isLoading: boolean) {
      state.isLoadingBookings = isLoading;
    },
    setSelectedWeek(
      state: DockBookingState,
      week: { startDate: Date; endDate: Date } | undefined,
    ) {
      state.selectedWeek = week;
    },
    setSelectedDockIds(state: DockBookingState, dockIds: string[] | undefined) {
      state.selectedDockIds = dockIds;
    },
  },
  actions: {
    async fetchBookings({ commit, state }: ActionType) {
      // Cancel any in-flight request for this store instance
      state.fetchAbortController?.abort();
      const controller = new AbortController();
      state.fetchAbortController = controller;

      commit('setLoadingBookings', true);

      try {
        const api = await getApi();
        const { data } = await api.listBookingsDockschedulingBookingsGet(
          state.selectedDockIds,
          state.selectedWeek?.startDate
            ? format(state.selectedWeek.startDate, 'yyyy-MM-dd')
            : undefined,
          state.selectedWeek?.endDate
            ? format(state.selectedWeek.endDate, 'yyyy-MM-dd')
            : undefined,
          { signal: controller.signal },
        );

        // Only commit if this request wasn't aborted
        if (!controller.signal.aborted) {
          commit('setBookings', data);
        }
      } catch (error: any) {
        // Skip reporting when we aborted the request ourselves (e.g. the user
        // changed the filter/date range and a newer fetch superseded this one).
        if (!controller.signal.aborted) {
          showError('dock-management.error-fetching-bookings');
          Sentry.captureException(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          commit('setLoadingBookings', false);
        }
      }
    },
    async removeBooking({ commit }: ActionType, bookingId: string) {
      try {
        commit('setLoadingBookings', true);
        const api = await getApi();
        await api.deleteBookingDockschedulingBookingsBookingIdDelete(bookingId);
        showSuccess('dock-management.success-deleting-booking');
      } catch (error) {
        showError('dock-management.error-deleting-booking');
        Sentry.captureException(error);
      } finally {
        commit('setLoadingBookings', false);
      }
    },

    async createBooking({ commit }: ActionType, payload: WriteBookingRequest) {
      try {
        commit('setLoadingBookings', true);
        const api = await getApi();
        await api.createBookingDockschedulingBookingsPost(payload);
        showSuccess('dock-management.success-creating-booking');
      } catch (error) {
        showError('dock-management.error-creating-booking');
        Sentry.captureException(error);
      } finally {
        commit('setLoadingBookings', false);
      }
    },

    async updateBooking(
      { commit }: ActionType,
      payload: WriteBookingRequest & { id: string },
    ) {
      try {
        commit('setLoadingBookings', true);
        const api = await getApi();
        if (!payload.id) {
          throw new Error('Booking ID is required for update');
        }
        await api.updateBookingDockschedulingBookingsBookingIdPut(
          payload.id,
          payload,
        );
        showSuccess('dock-management.success-updating-booking');
      } catch (error) {
        showError('dock-management.error-updating-booking');
        Sentry.captureException(error);
      } finally {
        commit('setLoadingBookings', false);
      }
    },
  },
};
